import { useState, useCallback, useMemo } from "react";
import * as Auth from 'aws-amplify/auth';
import { Typography } from "@mui/material";
import LoginHeader from "../Login/LoginHeader";
import ChangeAccount from "../Login/ChangeAccount";
import { cognitoFields } from "vccm-common";
import { TextInput } from "../../components-redesign/TextInput/TextInput";
import { SelectInput } from "../../components-redesign/SelectInput/SelectInput";
import PrimaryButton from "../../components-redesign/StyledButtons/PrimaryButton";
import ErrorAlert from "../../components-redesign/Error/ErrorAlert";
import PrivacyPolicy from "./PrivacyPolicy";
import { useLocalTranslationState } from "../../context/AppContext/AppContext";
import { styled } from "@mui/material/styles";
import profileLanguages from "../../utilities/profileLanguages";

const FormContainer = styled("form")(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
  [theme.breakpoints.down('sm')]: {
    minWidth: '100%',
  },
}));

const LoginBtnContainer = styled("div")(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-end',
  flexDirection: "column",
  paddingTop: theme.spacing(2),
}));

const TextContainer = styled("div")({
  alignSelf: "stretch",
});

const HeaderContainer = styled("div")(({ theme }) => ({
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(2),
}));

const ChangeAccountLinkContainer = styled("div")(({ theme }) => ({
  paddingBottom: theme.spacing(5),
}));

interface AddAccountDetailsProps {
  isLoading;
  setIsLoading: (loading) => void
  username: string;
  password: string;
  marketingEmails: number;
  onBack: () => void;
  setCreateAccountState: () => void;
  authError: string;
  setAuthError: (username) => void;
  setLoginState: (state) => void
}

const AddAccountDetails = ({
  isLoading,
  setIsLoading,
  username,
  password,
  marketingEmails,
  onBack,
  setCreateAccountState,
  authError,
  setAuthError,
  setLoginState,
}: AddAccountDetailsProps) => {

  const { _translate } = useLocalTranslationState();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [language, setLanguage] = useState("0");
  const [company, setCompany] = useState("");
  const [firstNameError, setFirstNameError] = useState("");
  const [lastNameError, setLastNameError] = useState("");
  const [companyError, setCompanyError] = useState("");
  const [languageError, setLanguageError] = useState("");
  const [, setUpdateUserAttributes] = useState<any>();
  const [emailAlreadyUsedError, setEmailAlreadyUsedError] = useState(false);

  const isValid = () => {
    if (!firstName) {
      setFirstNameError(_translate("Error, please complete this field"))
      return false
    }
    if (!lastName) {
      setLastNameError(_translate("Error, please complete this field"))
      return false
    }
    if (!company) {
      setCompanyError(_translate("Error, please complete this field"))
      return false
    }
    if (!language) {
      setLanguageError(_translate("Error, please complete this field"))
      return false
    }
    return true
  };

  const languageOptions = useMemo(() => {
    return profileLanguages.map((el) => ({
      value: String(el.LanguageId),
      text: _translate(el.LocalName),
    }));
  }, []);

  const signUp = useCallback(async (
    username: string,
    password: string,
    userAttributes: object,
  ) => {
    const updateUserAttributesOutput = await Auth.signUp({
      username,
      password,
      options: {
        userAttributes,
      }
    });

    return updateUserAttributesOutput;
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    if (isValid()) {
      try {

        // Store the locale string in Cognito. 
        // The "LanguageId" integer is originally used as the value in the SELECT to avoid collisions with the "" placeholder
        const IetflanguageTag = profileLanguages.find(({ LanguageId }) => LanguageId?.toString() === language)?.IetflanguageTag;

        const userAttributes = {
          email: username,
          given_name: firstName,
          family_name: lastName,
          [cognitoFields.COMPANY_NAME]: company,
          locale: IetflanguageTag ?? language,
          // Amplify/cognito requires numbers to be passed as strings
          "custom:receive_marketing": marketingEmails.toString()
        }
        const updateUserAttributesOutput = await signUp(
          username,
          password,
          userAttributes
        )
        setUpdateUserAttributes(updateUserAttributesOutput);
        setIsLoading(false);
        setCreateAccountState();
      } catch (error) {
        setIsLoading(false);
        console.log("Caught error:", error.message);
        setAuthError(error.message);
        if (error.message?.includes("User already exists") ||
          error.message?.includes("An account with the given email already exists")
        ) {
          setEmailAlreadyUsedError(true);
          setAuthError('');
        }
      }
    } else {
      setIsLoading(false);
    }
  };

  return (
    <>
      <FormContainer noValidate onSubmit={handleSubmit}>
        {emailAlreadyUsedError && (
          <ErrorAlert
            title={_translate("Account creation error")}
            subTitle={_translate("An account using the supplied email address already exists")}
            buttonText={_translate("Log in")}
            onClick={setLoginState}
          />
        )}
        <TextContainer>
          <Typography variant="subtitle2" fontWeight="400">
            {_translate("Step 3 of 4")}
          </Typography>
        </TextContainer>
        <HeaderContainer>
          <LoginHeader text={_translate("Tell us who you are")} />
        </HeaderContainer>
        <ChangeAccountLinkContainer>
          <ChangeAccount
            username={username}
            error={emailAlreadyUsedError}
            onClick={() => {
              setAuthError("")
              onBack()
            }} />
        </ChangeAccountLinkContainer>
        <TextInput
          fieldName="fname"
          id="fname"
          dataTestId="fname"
          label={_translate("First Name")}
          value={firstName}
          isDisabled={isLoading}
          isRequired={true}
          autoFocus={true}
          errorInPlainText={false}
          error={firstNameError}
          onClear={() => setFirstName('')}
          onChange={(event) => {
            const value = event.target.value;
            setFirstName(() => value);
            if (value) {
              setFirstNameError('')
            }
          }}
        />
        <TextInput
          fieldName="lname"
          id="lname"
          dataTestId="lname"
          label={_translate("Last Name")}
          value={lastName}
          isDisabled={isLoading}
          isRequired={true}
          errorInPlainText={false}
          error={lastNameError}
          onClear={() => setLastName('')}
          onChange={(event) => {
            const value = event.target.value;
            setLastName(() => value);
            if (value) {
              setLastNameError('')
            }
          }}
        />
        <TextInput
          fieldName="company"
          id="company"
          dataTestId="company"
          label={_translate("Company")}
          value={company}
          isDisabled={isLoading}
          isRequired={true}
          errorInPlainText={false}
          error={companyError}
          onClear={() => setCompany('')}
          onChange={(event) => {
            const value = event.target.value;
            setCompany(() => value);
            if (value) {
              setCompanyError('')
            }
          }}
        />
        <SelectInput
          fieldName="language"
          id="language"
          dataTestId="language"
          label={_translate("Preferred Language")}
          value={language}
          isDisabled={isLoading}
          isRequired={true}
          error={languageError || authError}
          options={languageOptions}
          onClear={() => setLanguage('')}
          onChange={(event) => {
            const value = event.target.value;
            setLanguage(() => value);
            if (value) {
              setLanguageError('')
            }
          }}
        />

        <LoginBtnContainer>
          <PrimaryButton
            id="login-next-btn"
            text={_translate("Create my account")}
            dataTestId="next-btn"
            isDisabled={emailAlreadyUsedError}
          />
        </LoginBtnContainer>

        <PrivacyPolicy />

      </FormContainer >
    </>
  );
};

export default AddAccountDetails;